import { ApiErrorCode, getApiErrorMessage } from "utils/errors";

import type {
  ArrangementConfig,
  ArrangementState,
  CaseDeathCertificateState,
  DcOrderingPreference,
  InclusionHighlight,
  PackageInclusions,
  QuoteBreakdown,
  QuoteInput,
  ServiceAnswer,
  ServiceQuestion,
  AddOnAnswer,
} from "./types";

/**
 * Format an ISO timestamp as a medium date + short time in the viewer's
 * locale. Returns "" for missing or unparseable input. Shared by the signed-at
 * and confirmed-at displays so the format stays consistent.
 */
export function formatIsoDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/**
 * Join a person's first + last name, falling back to `fallback` when neither
 * is present. Shared by the recipient and purchaser name displays.
 */
export function formatPersonName(
  person:
    | { first_name?: string | null; last_name?: string | null }
    | null
    | undefined,
  fallback = "",
): string {
  if (!person) return fallback;
  return (
    [person.first_name, person.last_name].filter(Boolean).join(" ") || fallback
  );
}

// Question ID constants used across the arrangement flow
export const QUESTION_ID = {
  WEIGHT: "q-weight",
  DEATH_CERTS: "q-death-certs",
  DELIVERY: "q-delivery",
  PACEMAKER: "q-pacemaker",
  FINGERPRINTS: "q-fingerprints",
  REFRIGERATION: "q-refrigeration",
  RUSH_CREMATION: "q-rush-cremation",
  // Client-side only: who places the death certificate order. Unlike every
  // other id here there is no matching entry in after-api's SERVICE_QUESTIONS,
  // so it never renders as its own Services screen — DeathCertificatesQuestion
  // owns it alongside the quantity. It rides the existing service_answers
  // seam (string value -> selectedOptionId), so it needs no serialization
  // change; see CLIENT_SIDE_QUESTION_IDS for the one place that does have to
  // know about it.
  DC_ORDERING: "q-dc-ordering",
} as const;

/**
 * Question ids the client owns, with no counterpart in the market's
 * `config.service_questions`.
 *
 * Anything that drops answers it can't match to a config question has to let
 * these through. Draft hydration is the one that matters: it filters persisted
 * answers against the config so a question retired from a market stops
 * resurfacing, and without this exemption a family who chose to order their
 * own certificates would silently revert to After ordering on reload.
 *
 * Read-only surfaces (StatementOfGoods, AdjustOrderDrawer) do the opposite and
 * correctly skip these — the preference isn't a priced line item.
 */
const CLIENT_SIDE_QUESTION_IDS: readonly string[] = [QUESTION_ID.DC_ORDERING];

export function isClientSideQuestionId(questionId: string): boolean {
  return CLIENT_SIDE_QUESTION_IDS.includes(questionId);
}

/**
 * Who orders the death certificates. Mirrors the `dc_ordering_preference`
 * values on the case model (ENG-1467). An absent answer means the family was
 * never asked, which is every case predating this screen — branch on
 * `is FAMILY`, never on `not AFTER`.
 */
export const DC_ORDERING = {
  AFTER: "after_orders",
  FAMILY: "family_orders",
} as const;

/**
 * Which option a family lands on before touching anything.
 *
 * A package that includes no certificates (Base) defaults to self-serve: there
 * is nothing for us to order and nothing to credit. Packages that include
 * certificates preselect nothing, so the family makes the choice.
 */
export function defaultDcOrdering(includedDeathCerts: number): string | null {
  return includedDeathCerts > 0 ? null : DC_ORDERING.FAMILY;
}

/** Why the family can't move past the death-certificate question yet. */
export const DC_HINT = {
  // The question stays closed rather than guessing: answering against unknown
  // case state could overwrite a choice the family already made. The message
  // must not promise an automatic recovery — a terminal failure (anything the
  // outer guard doesn't turn into a redirect) would make that promise false.
  READ_FAILED:
    "We couldn't check the death certificate status for this case. Please refresh the page, or reach our care team below if this keeps happening.",
  SAVE_FAILED: "We couldn't save your choice. Please try again.",
  LOCKED: getApiErrorMessage(
    ApiErrorCode.Arrangement.DC_ORDERING_PREFERENCE_LOCKED,
  ),
  EMPTY_ORDER: "Add at least one copy, or choose to order them yourself.",
  UNANSWERED: "Choose who orders the certificates.",
};

/** True when the family opted to order their own certificates. */
export function isFamilyOrderingCerts(answers: ServiceAnswer[]): boolean {
  return (
    answers.find((a) => a.questionId === QUESTION_ID.DC_ORDERING)
      ?.selectedOptionId === DC_ORDERING.FAMILY
  );
}

/**
 * Who the case says orders the certificates, or null when it has never been
 * decided.
 *
 * A case whose certificates we already ordered IS an After-orders case even
 * with no preference recorded — every case predating the question is in that
 * shape. Read the case through this rather than the raw field: treating a
 * legacy ordered case as "never asked" renders a self-serve default over an
 * executed order and sends a preference the lock will refuse forever.
 */
export function effectiveDcPreference(
  caseDc: CaseDeathCertificateState | null | undefined,
): DcOrderingPreference | null {
  if (!caseDc) return null;
  if (caseDc.dc_ordering_preference) return caseDc.dc_ordering_preference;
  return isDcOrderingLocked(caseDc) ? DC_ORDERING.AFTER : null;
}

/**
 * True once the certificates have been ordered. Both the preference and the
 * count then record an executed, paid order, so the flow renders them and
 * stops offering edits.
 */
export function isDcOrderingLocked(
  caseDc: CaseDeathCertificateState | null | undefined,
): boolean {
  return !!caseDc?.date_death_certificates_ordered;
}

/**
 * Service answers to write so the draft stops contradicting the case. Empty
 * when it already agrees, and empty whenever the family can still answer the
 * question themselves — a draft that disagrees is then simply a choice they
 * have not continued past yet, and moving past it writes both records.
 *
 * What remains is the case the family has no way to repair: the question is
 * read-only, or switched off entirely, and the confirmation gate refuses a
 * draft that disagrees with the case. Absence is meaningful there, so this is
 * deliberately not "make the draft mirror the case":
 * - `family_orders` is written even into an empty draft. It is the one value
 *   the gate demands explicitly, and with the question switched off nothing
 *   else in the flow can produce it.
 * - `after_orders` is written only over an answer that contradicts it. An
 *   absent answer already means After ordering, so writing one unprompted
 *   would only turn the meaningful no-choice state into a synthetic answer.
 * - the quantity is taken from the case only once the order exists, since only
 *   then does the case hold a count the family can no longer change.
 */
export function dcAnswerCorrections(
  serviceAnswers: ServiceAnswer[],
  caseDc: CaseDeathCertificateState,
  isQuestionAnswerable: boolean,
): ServiceAnswer[] {
  if (isQuestionAnswerable) return [];

  const preference = effectiveDcPreference(caseDc);
  const locked = isDcOrderingLocked(caseDc);
  const corrections: ServiceAnswer[] = [];

  const orderingAnswer = serviceAnswers.find(
    (a) => a.questionId === QUESTION_ID.DC_ORDERING,
  )?.selectedOptionId;
  if (
    preference !== null &&
    orderingAnswer !== preference &&
    (preference === DC_ORDERING.FAMILY || !!orderingAnswer)
  ) {
    corrections.push({
      questionId: QUESTION_ID.DC_ORDERING,
      selectedOptionId: preference,
    });
  }

  // Both sides normalized the way the confirmation gate normalizes them: an
  // unrecorded count on an order that was placed is a count of none, and so is
  // an absent answer. A locked case the draft can't be brought in line with is
  // a screen the family can only read and a gate they can never pass.
  const orderedQuantity = caseDc.number_of_death_certificates ?? 0;
  const quantityAnswer = serviceAnswers.find(
    (a) => a.questionId === QUESTION_ID.DEATH_CERTS,
  )?.quantity;
  if (locked && (quantityAnswer ?? 0) !== orderedQuantity) {
    corrections.push({
      questionId: QUESTION_ID.DEATH_CERTS,
      quantity: orderedQuantity,
    });
  }

  return corrections;
}

/**
 * Which option to show, guard against and write, or null while the case is
 * still unread or the family hasn't chosen yet.
 *
 * The draft answer leads, but only on a case that can still change: once the
 * certificates are ordered the case's own preference is the only truth, and the
 * draft is whatever was left behind. The package default comes last.
 */
export function resolveDcOrdering({
  caseDc,
  draftAnswer,
  packageInclusions,
}: {
  caseDc: CaseDeathCertificateState | null | undefined;
  draftAnswer: string | null | undefined;
  packageInclusions: PackageInclusions | null | undefined;
}): string | null {
  if (!caseDc) return null;

  const casePreference = effectiveDcPreference(caseDc);
  if (isDcOrderingLocked(caseDc)) return casePreference;

  return (
    draftAnswer ??
    casePreference ??
    (packageInclusions
      ? defaultDcOrdering(packageInclusions.includedDeathCerts)
      : null)
  );
}

/**
 * The reason to show under the Continue button on the death-certificate
 * question, or undefined when there is nothing holding the family up.
 */
export function dcContinueHint({
  writeBlock,
  isDcOrderPlaced,
  dcSelfServeEnabled,
  isCaseReadBlocked,
  hasCaseReadError,
}: {
  writeBlock: "save_failed" | "locked" | null;
  isDcOrderPlaced: boolean;
  dcSelfServeEnabled: boolean;
  isCaseReadBlocked: boolean;
  hasCaseReadError: boolean;
}): string | undefined {
  if (writeBlock === "locked" || (isDcOrderPlaced && dcSelfServeEnabled)) {
    return DC_HINT.LOCKED;
  }
  if (writeBlock === "save_failed") return DC_HINT.SAVE_FAILED;
  if (isCaseReadBlocked && hasCaseReadError) return DC_HINT.READ_FAILED;
  return undefined;
}

/**
 * Compute the cost for death certificate copies beyond the included count.
 * Returns 0 when quantity <= included.
 */
export function deathCertCost(
  quantity: number,
  included: number,
  firstCopyPrice: number,
  additionalCopyPrice: number,
): number {
  const extraCopies = Math.max(0, quantity - included);
  if (extraCopies === 0) return 0;
  return firstCopyPrice + (extraCopies - 1) * additionalCopyPrice;
}

/**
 * What the certificates a package includes are worth, priced at the market's
 * own per-copy rate. This is the amount credited back when the family orders
 * their own.
 *
 * NOTE: this credits After's list price, not the county's charge. The two
 * differ, and the county's differs again by county. Confirm with Brian before
 * the credit is actually applied to a quote (ENG-1468).
 */
export function selfServeCreditAmount(
  includedQuantity: number,
  firstCopyPrice: number,
  additionalCopyPrice: number,
): number {
  return deathCertCost(
    includedQuantity,
    0,
    firstCopyPrice,
    additionalCopyPrice,
  );
}

/**
 * Returns true if the question represents a pass-through/government fee
 * rather than an After service fee.
 */
export function isPassThroughFee(questionId: string): boolean {
  return questionId === QUESTION_ID.DEATH_CERTS;
}

/**
 * The pass-through fee answers a surface should list. A family ordering their
 * own certificates buys none from us: the quote carries no copies and the
 * order total no charge for them, so listing the drafted quantity and its
 * price would not foot to the total.
 */
export function visiblePassThroughFeeAnswers(
  answers: ServiceAnswer[],
): ServiceAnswer[] {
  if (isFamilyOrderingCerts(answers)) return [];
  return answers.filter((answer) => isPassThroughFee(answer.questionId));
}

/**
 * The service answers that contribute to the client-side total, used when no
 * server quote is available. A family ordering their own certificates buys
 * none from us, so the pass-through certificate answer is not one of them: the
 * quote is sent with a count of zero and its fee rows are hidden, and charging
 * a drafted quantity here would disagree with both.
 */
// A package inclusion line is marketing copy with no reference to the offering
// it describes, so the only thing tying one to the certificates is its own
// wording. Renaming a line in the catalog silently stops the filter below; the
// structured fix is an offering reference on the highlight itself.
const DEATH_CERTIFICATE_HIGHLIGHT = /death certificate/i;

/**
 * The package inclusion lines to list. A family ordering their own
 * certificates receives neither the copies the package includes nor our
 * ordering service, so listing them beside the credit contradicts it.
 */
export function visibleInclusionHighlights(
  highlights: InclusionHighlight[],
  answers: ServiceAnswer[],
): InclusionHighlight[] {
  if (!isFamilyOrderingCerts(answers)) return highlights;
  return highlights.filter(
    (highlight) => !DEATH_CERTIFICATE_HIGHLIGHT.test(highlight.title),
  );
}

export function chargeableServiceAnswers(
  answers: ServiceAnswer[],
): ServiceAnswer[] {
  if (!isFamilyOrderingCerts(answers)) return answers;
  return answers.filter((answer) => !isPassThroughFee(answer.questionId));
}

export function isShippingAddressComplete(
  address: ArrangementState["fulfillment"]["shippingAddress"],
): boolean {
  return (
    address.recipientName.trim() !== "" &&
    address.street.trim() !== "" &&
    address.city.trim() !== "" &&
    address.state.trim() !== "" &&
    address.zip.trim() !== ""
  );
}

/**
 * Get the included quantity for an offering from package inclusions.
 * Mirrors backend getIncludedOfferingQuantity in arrangement-package-inclusions.ts.
 */
export function getIncludedOfferingQuantity(
  inclusions: PackageInclusions | null,
  offeringId: string | undefined,
): number {
  if (!inclusions?.includedOfferings || !offeringId) return 0;
  const match = inclusions.includedOfferings.find(
    (o) => o.offering_id === offeringId,
  );
  return match?.quantity ?? 0;
}

/**
 * Whether the selected package covers fingerprint preservation. Inclusion is
 * pricing data, not a tier rule: the offering id is market-specific, so a plan
 * that includes it in one market may not in another. Shared so the heading that
 * announces it and the card that prices it can't disagree.
 */
export function isFingerprintPreservationIncluded(
  config: ArrangementConfig | null,
  inclusions: PackageInclusions | null,
): boolean {
  const question = config?.service_questions.find(
    (q) => q.id === QUESTION_ID.FINGERPRINTS,
  );
  const yesOption = question?.options?.find((o) => o.id === "yes");
  return getIncludedOfferingQuantity(inclusions, yesOption?.offeringId) > 0;
}

/**
 * Yes/no service questions that bill only on a "yes" answer, mapped to the
 * display label used in place of the option label on review surfaces.
 * Membership doubles as the "yes-only" predicate.
 */
export const YES_ONLY_SERVICE_LABELS: Partial<Record<string, string>> = {
  [QUESTION_ID.PACEMAKER]: "Pacemaker Removal",
  [QUESTION_ID.FINGERPRINTS]: "Fingerprint Preservation",
  [QUESTION_ID.RUSH_CREMATION]: "Rush Cremation",
};

/**
 * Find the market's Rush Cremation offering-variant id off the "yes" option
 * of the q-rush-cremation service question. Null when the question, its yes
 * option, or the variant id isn't present in config.
 */
export function getRushCremationVariantId(
  config: ArrangementConfig | null,
): string | null {
  const question = config?.service_questions.find(
    (q) => q.id === QUESTION_ID.RUSH_CREMATION,
  );
  const yesOption = question?.options?.find((o) => o.id === "yes");
  return yesOption?.variantId ?? null;
}

/**
 * Service answers that render as a visible line item under "Additional
 * Services": government/pass-through fees are shown elsewhere, and yes-only
 * questions (rush cremation, fingerprint, pacemaker) only count when
 * answered "yes" with a priced option. Shared by StatementOfGoods and
 * AdjustOrderDrawer so a yes-only question answered "no" (or unpriced) can
 * never leave a section header rendered with no rows under it.
 */
export function filterVisibleServiceAnswers(
  serviceAnswers: ServiceAnswer[],
  serviceQuestions: ServiceQuestion[],
): ServiceAnswer[] {
  return serviceAnswers.filter((a) => {
    if (isPassThroughFee(a.questionId)) return false;
    const question = serviceQuestions.find((q) => q.id === a.questionId);
    if (!question) return false;
    if (a.questionId in YES_ONLY_SERVICE_LABELS) {
      if (a.selectedOptionId !== "yes") return false;
      const yesOption = question.options?.find((o) => o.id === "yes");
      return (yesOption?.price ?? 0) > 0;
    }
    return true;
  });
}

/**
 * Extract quote-derived fee line items, plus the credits for inclusions the
 * family declined, from the breakdown. Credits come back positive — the
 * breakdown's total already has them deducted, so a surface that lists them
 * renders them negative. Used by StatementOfGoods and AdjustOrderDrawer for
 * consistent display.
 */
export function extractQuoteFees(breakdown: QuoteBreakdown | null): {
  items: { label: string; amount: number }[];
  specialFees: number;
  stateCountyFees: number;
  credits: { label: string; amount: number }[];
  creditTotal: number;
} {
  if (!breakdown) {
    return {
      items: [],
      specialFees: 0,
      stateCountyFees: 0,
      credits: [],
      creditTotal: 0,
    };
  }

  // Pacemaker and fingerprint are user-chosen service answers, already
  // rendered in "Additional Services". Weight, inurnment, transport, and the
  // death-cert admin fee (only charged on plans that don't include certs in
  // the package, e.g. Base) are automatic — they appear here under the
  // subtotal since they aren't listed elsewhere.
  //
  // TODO: Surface the storage/refrigeration fee on the confirmation UI.
  // The SFG currently bills accrued storage ($75/day beyond the plan's
  // included days) that the customer never sees on the Arrangement Review,
  // causing the two totals to diverge. The quote breakdown already carries
  // a `storage` object, but the arrangement quote at selection time doesn't
  // know the actual accrued days — fee sync computes the real number at
  // document creation time against the case's pickup-to-cremation window.
  // Options: (a) fetch post-sync case products and show them on the
  // confirmation step, or (b) have the quote engine project a forecast
  // based on expected cremation window. Decide before shipping broadly.
  const feeMap: { key: keyof QuoteBreakdown; label: string }[] = [
    { key: "inurnment_fee", label: "Split Fees" },
    { key: "weight_fee", label: "Weight Handling Fee" },
    { key: "transport_fee", label: "Transport Fee" },
    {
      key: "death_certificate_processing_fee",
      label: "Death Certificate Admin Fee",
    },
  ];

  const items: { label: string; amount: number }[] = [];
  let specialFees = 0;

  for (const { key, label } of feeMap) {
    const amount = (breakdown[key] as number) || 0;
    if (amount > 0) {
      items.push({ label, amount });
      specialFees += amount;
    }
  }

  const stateCountyFees =
    breakdown.state_county_fees > 0 ? breakdown.state_county_fees : 0;

  const inclusionCredits = breakdown.inclusion_credits ?? [];
  const credits = inclusionCredits.map((credit) => ({
    label: `${credit.name} credit`,
    amount: credit.amount,
  }));
  const creditTotal = inclusionCredits.reduce(
    (sum, credit) => sum + credit.amount,
    0,
  );

  return { items, specialFees, stateCountyFees, credits, creditTotal };
}

/**
 * The subtotal the statement shows above the lines it lists separately: the
 * order total with those lines taken back out. The total is already net of the
 * credits, so they are added back for the listed lines to foot to it.
 */
export function quoteSubtotal(
  quoteTotal: number,
  fees: { specialFees: number; stateCountyFees: number; creditTotal: number },
): number {
  return (
    quoteTotal - fees.stateCountyFees - fees.specialFees + fees.creditTotal
  );
}

/**
 * Compute the cost contribution of a single service answer, accounting for
 * package inclusions (delivery credit, included death certs).
 */
export function computeServiceAnswerCost(
  answer: ServiceAnswer,
  question: ServiceQuestion,
  inclusions: PackageInclusions | null,
): number {
  if (question.type === "radio" && answer.selectedOptionId) {
    const option = question.options?.find(
      (o) => o.id === answer.selectedOptionId,
    );
    if (option) {
      // Generic offering inclusion (delivery, pacemaker, fingerprint, etc.).
      // Each option carries its offering_id; if the plan includes that offering, it's free.
      if (
        option.offeringId &&
        getIncludedOfferingQuantity(inclusions, option.offeringId) > 0
      ) {
        return 0;
      }
      return option.price;
    }
  } else if (
    question.type === "stepper" &&
    answer.quantity &&
    answer.quantity > 0 &&
    question.stepperConfig
  ) {
    const { firstCopyPrice, additionalCopyPrice } = question.stepperConfig;
    const included = inclusions?.includedDeathCerts ?? 0;
    return deathCertCost(
      answer.quantity,
      included,
      firstCopyPrice,
      additionalCopyPrice,
    );
  }
  return 0;
}

/**
 * Map current arrangement selections to the quote API input shape.
 * Returns null if no package is selected (quote not possible).
 *
 * Accepts individual fields instead of the whole state so that
 * useMemo dependency arrays are provably in sync.
 */
export function mapSelectionsToQuoteInput(
  selectedPackageId: string | null,
  serviceAnswers: ServiceAnswer[],
  addOnSelections: AddOnAnswer[],
  hasIncludedUrn: boolean,
  additionalUrnCount: number,
  fillableKeepsakeCount: number,
): QuoteInput | null {
  if (!selectedPackageId) return null;

  // Extract delivery answer
  const deliveryAnswer = serviceAnswers.find(
    (a) => a.questionId === QUESTION_ID.DELIVERY,
  );
  const deliveryType = deliveryAnswer?.selectedOptionId ?? null;

  // Extract death certificate count. A family ordering their own is zero
  // regardless of what the stepper last held, so switching to self-serve can
  // never leave a stale paid quantity on the quote.
  const deathCertAnswer = serviceAnswers.find(
    (a) => a.questionId === QUESTION_ID.DEATH_CERTS,
  );
  const deathCertificates = isFamilyOrderingCerts(serviceAnswers)
    ? 0
    : (deathCertAnswer?.quantity ?? 0);

  // Map add-on selections — only include items with quantity > 0
  const additionalServices = addOnSelections
    .filter((sel) => sel.quantity > 0)
    .map((sel) => ({
      variant_id: sel.serviceId,
      quantity: sel.quantity,
    }));

  // Inurnments = total fillable containers - 1.
  const totalFillableContainers =
    (hasIncludedUrn ? 1 : 0) + additionalUrnCount + fillableKeepsakeCount;
  const inurnments = Math.max(0, totalFillableContainers - 1);

  // Pacemaker, fingerprint, and rush cremation
  const pacemakerAnswer = serviceAnswers.find(
    (a) => a.questionId === QUESTION_ID.PACEMAKER,
  );
  const fingerprintAnswer = serviceAnswers.find(
    (a) => a.questionId === QUESTION_ID.FINGERPRINTS,
  );
  const rushAnswer = serviceAnswers.find(
    (a) => a.questionId === QUESTION_ID.RUSH_CREMATION,
  );

  // The option ids are the preference values themselves, so anything the
  // question didn't produce is left off rather than sent on.
  const dcOrderingAnswer = serviceAnswers.find(
    (a) => a.questionId === QUESTION_ID.DC_ORDERING,
  )?.selectedOptionId;
  const dcOrderingPreference = Object.values(DC_ORDERING).find(
    (preference) => preference === dcOrderingAnswer,
  );

  return {
    selected_package_variant_id: selectedPackageId,
    delivery_type: deliveryType,
    death_certificates: deathCertificates,
    additional_services: additionalServices,
    inurnments,
    pacemaker: pacemakerAnswer?.selectedOptionId === "yes",
    collect_fingerprints: fingerprintAnswer?.selectedOptionId === "yes",
    rush_cremation: rushAnswer?.selectedOptionId === "yes",
    ...(dcOrderingPreference
      ? { dc_ordering_preference: dcOrderingPreference }
      : {}),
  };
}
